import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiVolume2, FiAlertTriangle, FiCheckCircle, FiArrowLeft, 
  FiInfo, FiX, FiShield, FiAnchor, FiLayers, FiAlertCircle,
  FiTrendingDown, FiTool, FiCheck, FiSearch, FiBriefcase
} = FiIcons;

function SoundInsulation() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Schallschutz bei Holzbalkendecken: Warum Mineralwolle allein ein Fehler ist",
        "alternativeHeadline": "Trittschall vs. Luftschall: Warum Masse im Altbau entscheidend ist",
        "image": "https://mx.ausbau.nrw/img/Trocken_und_Akustikbau_xl.jpg",
        "author": {
          "@type": "Person",
          "name": "Maxim Kelm",
          "jobTitle": "Geschäftsführer MK-AUSBAU",
          "url": "https://ausbau.nrw/ueber"
        },
        "publisher": {
          "@type": "Organization",
          "name": "MK-AUSBAU",
          "logo": {
            "@type": "ImageObject",
            "url": "https://ausbau.nrw/logo-icon.svg"
          }
        },
        "datePublished": "2026-01-16",
        "dateModified": "2026-03-25",
        "description": "Schallschutz im Altbau verbessern? 🚫 Nur Wolle reicht nicht! Erfahren Sie, warum Masse (Schüttung) gegen Trittschall entscheidend ist.",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://ausbau.nrw/#/blog/schallschutz-holzbalkendecke"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Hilft Mineralwolle gegen Trittschall in der Holzbalkendecke?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Kaum. Mineralwolle ist zu leicht. Sie dämpft zwar den Hohlraumklang (Trommeleffekt) und Luftschall (Stimmen), aber gegen tieffrequenten Trittschall (Gehgeräusche) hilft physikalisch nur Masse (Gewicht) oder Entkopplung."
            }
          },
          {
            "@type": "Question",
            "name": "Was ist die beste Schüttung für Schallschutz?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ideal sind schwere, gebundene Schüttungen (z.B. Waben-Schüttungen, Splitt oder spezielle Schallschutzgranulate mit hoher Rohdichte), die Masse in die Decke bringen, ohne zu rieseln."
            }
          }
        ]
      }
    ]
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Schallschutz Holzbalkendecke: Masse vs. Wolle | Experten-Guide 2026"
        description="Trittschall verbessern? 🛑 Achtung: Mineralwolle allein verschlechtert oft den Schallschutz! Warum Sie Masse (Schüttung) & Entkopplung brauchen."
        keywords="Schallschutz Holzbalkendecke, Trittschall sanieren, Mineralwolle vs Schüttung, Masse-Feder-System, Decke entkoppeln, Fehlboden sanieren, DIN 4109 Altbau"
        schema={articleSchema}
        canonical="/blog/schallschutz-holzbalkendecke"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/blog" 
          className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article {...fadeIn} className="prose prose-lg prose-mk max-w-none">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4">
              <SafeIcon icon={FiVolume2} />
              <span>Ratgeber: Bauphysik & Akustik</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Schallschutz bei Holzbalkendecken: Warum Mineralwolle allein ein großer Fehler ist
            </h1>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Experten-Ratgeber</span>
              <span className="flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Aktualisiert: März 2026</span>
            </div>
          </header>

          {/* Intro Video Stream */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl bg-black aspect-video relative">
            <video 
              src="https://canv.wasacon.com/mk-ausbau/_assets/video/aa52708dd16e259ad47564a0dbe3e256.mp4" 
              className="absolute top-0 left-0 w-full h-full object-cover"
              controls
              playsInline
              preload="metadata"
              poster="https://mx.ausbau.nrw/img/Trocken_und_Akustikbau_xl.jpg"
            >
              Ihr Browser unterstützt dieses Video-Format nicht.
            </video>
          </div>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12">
            Viele möchten bei der Altbausanierung „alles richtig machen“ – und bauen sich dabei ungewollt eine <strong>Schallschutz-Katastrophe</strong> ein. Besonders häufig passiert das bei Holzbalkendecken: Decke öffnen, alte Füllung raus, Mineralwolle rein, alles schön zugemacht – und am Ende ist der Schallschutz deutlich schlechter als vorher.
          </div>

          {/* Section 1: Das Szenario */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Das typische Szenario: „Wir sanieren mal eben die Decke“
          </h2>
          <p className="text-gray-700 mb-6">
            In der Praxis begegnet uns immer wieder eine ähnliche Situation: Wir sanieren einen Altbau mit Holzbalkendecken. In der Decke steckt „irgendwas“: alte Zeitungen, Schutt, Lehmfüllung – oder sie ist fast leer.
          </p>
          <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-mk-teal-500 mb-10">
            <p className="text-gray-900 font-medium mb-2">Die Standardfrage lautet:</p>
            <p className="italic text-gray-600">
              „Was packen wir da am besten rein? Mineralwolle? Das ist doch super für den Schallschutz, oder?“
            </p>
            <p className="text-mk-red-600 font-bold mt-4 flex items-center">
              <SafeIcon icon={FiX} className="mr-2" /> Genau hier beginnt der Denkfehler.
            </p>
          </div>

          {/* Section 2: Luftschall vs Trittschall */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiAlertCircle} className="mr-3 text-mk-teal-600" />
            Warum Mineralwolle zur Schallschutz-Falle wird
          </h2>
          <p className="text-gray-700 mb-8">
            Die Überlegung wirkt logisch: In Trockenbauwänden und abgehängten Decken steckt Mineralwolle. Überall heißt es: „Mineralwolle ist gut für Schallschutz.“ Warum also nicht die Holzbalkendecke damit füllen? <strong>Weil Decken andere Anforderungen haben als Wände.</strong>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white border-2 border-mk-teal-100 rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-4 text-mk-teal-600">
                <SafeIcon icon={FiVolume2} className="w-8 h-8 mr-3" />
                <h3 className="text-lg font-bold m-0">Luftschall</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Stimmen, Musik, Fernseher. Schallwellen, die durch die Luft übertragen werden.
              </p>
              <div className="bg-mk-teal-50 text-mk-teal-800 text-xs font-bold px-3 py-2 rounded-lg inline-block">
                Hier hilft Mineralwolle ✅
              </div>
            </div>

            <div className="bg-white border-2 border-mk-red-100 rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-4 text-mk-red-600">
                <SafeIcon icon={FiTrendingDown} className="w-8 h-8 mr-3" />
                <h3 className="text-lg font-bold m-0">Trittschall</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Schritte, Stühlerücken, hüpfende Kinder. Stoßimpulse, die die Konstruktion zum Schwingen bringen.
              </p>
              <div className="bg-mk-red-50 text-mk-red-800 text-xs font-bold px-3 py-2 rounded-lg inline-block">
                Hier versagt Mineralwolle ❌
              </div>
            </div>
          </div>

          {/* Section 3: Gewicht ist King */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiAnchor} className="mr-3 text-mk-teal-600" />
            „Gewicht ist King“ – Warum alte Decken oft besser waren
          </h2>
          <p className="text-gray-700 mb-6">
            Holzbalkendecken hatten ursprünglich häufig schwere Lehm-Stroh-Schüttungen oder Schlacke. Diese Schichten hatten eine klare Funktion: Sie brachten <strong>Gewicht (Masse)</strong> in die Decke.
          </p>
          
          <div className="bg-gray-900 text-white p-8 rounded-2xl shadow-xl mb-12">
            <h3 className="text-mk-teal-400 font-bold mt-0 mb-4">Das physikalische Gesetz der Masse:</h3>
            <p className="text-gray-300 leading-relaxed mb-6">
              Um Trittschall wirksam zu reduzieren, braucht es Masse. Mineralwolle ist extrem leicht (ca. 20-50 kg/m³). Eine alte Schüttung wiegt oft 1.200 kg/m³ oder mehr.
            </p>
            <div className="bg-white/10 p-4 rounded-lg border-l-4 border-mk-red-500">
              <p className="m-0 font-medium text-white">
                <strong>Das fatale Ergebnis:</strong> Wer die schwere Füllung entfernt und durch leichte Wolle ersetzt, macht die Decke zur Trommel. Der Schallschutz wird schlechter als vor der Sanierung!
              </p>
            </div>
          </div>

          {/* Section 4: Rechtliches */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Die rechtliche Falle: Bestandsschutz vs. Mangel
          </h2>
          <p className="text-gray-700 mb-6">
            Holzbalkendecken unterliegen Normen (z.B. DIN 4109). Das reine Austauschen gegen Mineralwolle kann dazu führen, dass nicht einmal mehr die Mindestanforderungen der Bauzeit eingehalten werden.
          </p>
          <div className="flex bg-mk-red-50 p-4 rounded-lg border border-mk-red-200 mb-8">
            <SafeIcon icon={FiAlertTriangle} className="text-mk-red-600 mt-1 mr-3 flex-shrink-0 w-6 h-6" />
            <div className="text-sm text-mk-red-800">
              <strong>Vorsicht:</strong> Altbauten haben Bestandsschutz. Aber wenn Sie eine Konstruktion aktiv verschlechtern, erlischt dieser oft und es entsteht ein Baumangel – obwohl Sie Geld investiert haben!
            </div>
          </div>

          {/* Section 5: Die Lösung */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiCheckCircle} className="mr-3 text-mk-teal-600" />
            Wie macht man es richtig? (Der 2-Schritte-Plan)
          </h2>
          <p className="text-gray-700 mb-8">
            Wenn wir eine Holzbalkendecke ertüchtigen, gilt der Grundsatz: Zuerst in Masse denken, dann in Dämmung.
          </p>

          <div className="space-y-8 mb-12">
            {/* Schritt 1 */}
            <div className="flex flex-col md:flex-row gap-6 bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-mk-teal-500 text-white rounded-full flex items-center justify-center font-bold text-xl">1</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mt-0 mb-3">Masse ins Gefach bringen</h3>
                <p className="text-gray-700 mb-3">
                  In die Hohlräume gehört Gewicht. Nutzen Sie schwere Schüttungen (z.B. Waben-Schüttung, Splitt, mineralische Granulate).
                </p>
                <ul className="space-y-2 mb-0">
                  <li className="flex items-center text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2" /> Hohes Gewicht pro m³ beachten
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2" /> Hohlräume vermeiden (Resonanzkörper)
                  </li>
                </ul>
              </div>
            </div>

            {/* Schritt 2 */}
            <div className="flex flex-col md:flex-row gap-6 bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-mk-teal-500 text-white rounded-full flex items-center justify-center font-bold text-xl">2</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mt-0 mb-3">Mineralwolle am richtigen Ort</h3>
                <p className="text-gray-700 mb-3">
                  Mineralwolle ist nicht schlecht – nur am falschen Ort wirkungslos. Nutzen Sie sie ergänzend zur Entkopplung.
                </p>
                <ul className="space-y-2 mb-0">
                  <li className="flex items-center text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2" /> In abgehängten Unterdecken
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2" /> In Vorsatzschalen
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section 6: Planung */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Planung statt Bauchgefühl: So gehen wir vor
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-mk-teal-50 p-6 rounded-xl">
              <SafeIcon icon={FiX} className="w-8 h-8 text-mk-red-500 mb-4" />
              <h4 className="font-bold text-gray-900 mb-2">Kein "Irgendwas"</h4>
              <p className="text-sm text-gray-600">Keine spontanen Baumarkt-Lösungen. Holzbalkendecken sind bauphysikalisch anspruchsvoll.</p>
            </div>
            <div className="bg-mk-teal-50 p-6 rounded-xl">
              <SafeIcon icon={FiSearch} className="w-8 h-8 text-mk-teal-600 mb-4" />
              <h4 className="font-bold text-gray-900 mb-2">Fachplanung</h4>
              <p className="text-sm text-gray-600">Wir nutzen geprüfte Systemaufbauten und Datenblätter, keine Experimente.</p>
            </div>
            <div className="bg-mk-teal-50 p-6 rounded-xl">
              <SafeIcon icon={FiBriefcase} className="w-8 h-8 text-mk-teal-600 mb-4" />
              <h4 className="font-bold text-gray-900 mb-2">Zieldefinition</h4>
              <p className="text-sm text-gray-600">Reicht die Norm oder soll es Komfort-Schallschutz sein? Das entscheidet den Aufbau.</p>
            </div>
          </div>

          {/* Conclusion */}
          <div className="bg-mk-teal-600 text-white p-10 rounded-[2.5rem] shadow-2xl mt-16 mb-12 text-center">
            <h2 className="text-white text-3xl font-bold mb-6 mt-0">Fazit: Masse statt Mythen</h2>
            <p className="text-mk-teal-100 text-lg mb-8 leading-relaxed max-w-2xl mx-auto">
              Wer „nach Gefühl“ saniert, riskiert eine Schallschutz-Katastrophe, die später nur extrem teuer zu reparieren ist. Eine fundierte Planung ist deutlich günstiger als eine „Sanierung der Sanierung“.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-white flex items-center">
                <SafeIcon icon={FiCheck} className="mr-2" /> Gewicht ist King
              </span>
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-white flex items-center">
                <SafeIcon icon={FiCheck} className="mr-2" /> Entkopplung beachten
              </span>
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-white flex items-center">
                <SafeIcon icon={FiCheck} className="mr-2" /> Fachgerecht planen
              </span>
            </div>
          </div>

          <div className="text-center py-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Planen Sie eine Deckensanierung?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Unsicher, ob Ihre Decke hält oder wie der Aufbau aussehen muss? Wir messen, prüfen und sanieren Ihre Holzbalkendecke fachgerecht in Heinsberg und Umgebung.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/kontakt" 
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Beratungstermin vereinbaren
              </Link>
              <Link 
                to="/trockenbau" 
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Mehr zu Trockenbau & Akustik
              </Link>
            </div>
          </div>
        </motion.article>
      </div>
      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default SoundInsulation;